import api from '../api';
import type {
  AdminReferralOverview,
  AdminTopReferrer,
  AdminReferralChannel,
  AdminReferralGeo,
  AdminReferralReward,
  AdminReferralFraud,
  AdminReferralBlacklistEntry,
  AdminAffiliatePayout,
  AdminReferralProgram,
  AdminTrackingCode,
} from '../../types/admin';

export const referralAdminService = {
  async getOverview(params?: { from?: string; to?: string }): Promise<AdminReferralOverview> {
    const response = await api.get('/api/admin/referrals/overview', { params });
    return response.data;
  },

  async getTopReferrers(params?: { period?: 'monthly' | 'all_time'; from?: string; to?: string }): Promise<{ leaderboard: AdminTopReferrer[] }> {
    const response = await api.get('/api/admin/referrals/top-referrers', { params });
    return response.data;
  },

  async getFunnel(params?: { from?: string; to?: string }): Promise<{ funnel: { clicks: number; signups: number; verified: number; conversions: number } }> {
    const response = await api.get('/api/admin/referrals/funnel', { params });
    return response.data;
  },

  async getChannels(params?: { from?: string; to?: string }): Promise<{ channels: AdminReferralChannel[] }> {
    const response = await api.get('/api/admin/referrals/channels', { params });
    return response.data;
  },

  async getGeo(params?: { from?: string; to?: string }): Promise<{ geo: AdminReferralGeo[] }> {
    const response = await api.get('/api/admin/referrals/geo', { params });
    return response.data;
  },

  async getRewards(params?: { status?: string; per_page?: number }) {
    const response = await api.get('/api/admin/referrals/rewards', { params });
    return response.data as { rewards: { data: AdminReferralReward[] } };
  },

  async getFraud(params?: { per_page?: number }) {
    const response = await api.get('/api/admin/referrals/fraud', { params });
    return response.data as { data: AdminReferralFraud[] };
  },

  async reviewFraud(referralId: number, payload: { review_status: 'approved' | 'rejected' | 'blocked'; fraud_reason?: string }) {
    const response = await api.post(`/api/admin/referrals/fraud/${referralId}/review`, payload);
    return response.data;
  },

  async getBlacklist(params?: { per_page?: number }) {
    const response = await api.get('/api/admin/referrals/blacklist', { params });
    return response.data as { data: AdminReferralBlacklistEntry[] };
  },

  async addBlacklist(payload: { type: 'email' | 'ip' | 'user_id'; value: string; reason?: string }) {
    const response = await api.post('/api/admin/referrals/blacklist', payload);
    return response.data as { entry: AdminReferralBlacklistEntry };
  },

  async deleteBlacklist(entryId: number) {
    const response = await api.delete(`/api/admin/referrals/blacklist/${entryId}`);
    return response.data;
  },

  async getPayouts(params?: { status?: string; per_page?: number }) {
    const response = await api.get('/api/admin/referrals/payouts', { params });
    return response.data as { data: AdminAffiliatePayout[] };
  },

  async markPayoutPaid(payoutId: number, payload?: { method?: 'paypal' | 'stripe'; payout_details?: Record<string, unknown> }) {
    const response = await api.post(`/api/admin/referrals/payouts/${payoutId}/mark-paid`, payload ?? {});
    return response.data;
  },

  async getPrograms(params?: { account_id?: number }) {
    const response = await api.get('/api/admin/referrals/programs', { params });
    return response.data as { data: AdminReferralProgram[] };
  },

  async updateProgram(programId: number, payload: Partial<AdminReferralProgram>) {
    const response = await api.put(`/api/admin/referrals/programs/${programId}`, payload);
    return response.data as { program: AdminReferralProgram };
  },

  async getTrackingCodes(): Promise<{ codes: AdminTrackingCode[] }> {
    const response = await api.get('/api/admin/referrals/tracking-codes');
    return response.data;
  },

  async createTrackingCode(payload: { label: string; code?: string; max_uses?: number | null; expires_at?: string | null }): Promise<{ code: AdminTrackingCode }> {
    const response = await api.post('/api/admin/referrals/tracking-codes', payload);
    return response.data;
  },

  async deleteTrackingCode(codeId: number): Promise<void> {
    await api.delete(`/api/admin/referrals/tracking-codes/${codeId}`);
  },
};

export default referralAdminService;
